/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import jwt_decode from "jwt-decode";

import { ConfigurationVariables } from "../../config";
import OrganizationDTO from "../../models/apiModels/OrganizationDTO";
import { getAccessToken } from "../Tenant/tenant-actions";
import UserPermissions from "../../models/apiModels/UserPermissions";
import { UUID } from "../../models/apiModels/Organization";
import {
  PermissionSet,
  WrapperOrganisationPermissions,
  WrapperUserPermissions,
} from "../../models";
import getWrappedPermissionsForOrgUser from "../../utils/methods/permissionsWrapper/getWrappedPermissionsForOrgUser";

///////////////////////////Get Organization
export const getOrganizationByTenantID = createAsyncThunk(
  "organization/getOrganizationByTenantID",

  async (
    tenantInfoToBePassed: {
      tenantID: string;
      userPermissions: WrapperUserPermissions;
    },
    { rejectWithValue }
  ) => {
    try {
      const { tenantID, userPermissions } = tenantInfoToBePassed;

      const config = {
        headers: {
          // tenantId: tenantID,
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: any = await axios.get(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.TenantController}/${tenantID}/${ConfigurationVariables.OrganisationController}?isUserCountRequired=true`,
        config
      );

      const convertToTreeStructure = (data: any) => {
        const tree: any = [];
        const map: any = {};

        data.forEach((node: any) => {
          map[node.id] = { ...node, children: [] };
        });

        data.forEach((node: OrganizationDTO) => {
          // map[node.organizationId] = map[node.organizationId] || {
          //   ...node,
          //   children: [],
          // };
          if (node.parentId !== null) {
            map[node.parentId]?.children.push(map[node.id]);
          } else {
            tree.push(map[node.id]);
          }
        });

        return tree;
      };

      const convertToTreewithKeys = (jsonData: any) => {
        const map = new Map();
        const result: any = [];

        // This map contains only values for which user is admin of organization
        jsonData.forEach((item: any) => {
          //if (item.isAdmin !== null || canViewAllOrgs(userPermissions)) {
          map.set(item.id, { ...item, children: [] });
          // }
        });

        jsonData.forEach((item: OrganizationDTO) => {
          const parent = map.get(item.parentId);
          // If isAdmin is true or false then only push. Dont push if isAdmin=null
          //if (item.isAdmin !== null || canViewAllOrgs(userPermissions)) {
          if (parent) {
            parent.children.push(map.get(item.id));
          } else {
            result.push(map.get(item.id));
          }
        });

        function convertNode(item: any, index = "") {
          const key = `${index}`;
          // need to use wrapper for org permissions

          const permissionSet: WrapperOrganisationPermissions | null =
            getWrappedPermissionsForOrgUser(item?.permissionSet?.result);

          return {
            key: key,
            label: item?.name,
            data: item?.id,
            parentId: item?.parentId,
            permissionSet: permissionSet,
            selectable:
              permissionSet === null
                ? false
                : permissionSet?.can_read_organisation_user,
            version: item?.version,
            children: item?.children.map((child: any, childIndex: any) =>
              convertNode(child, key + `-${childIndex}`)
            ),
          };
        }
        return result.map((item: any, index: any) => convertNode(item, index));
      };

      const convertedData = convertToTreeStructure(response.data);

      const convertToTreewithKeysValue: any = convertToTreewithKeys(
        response.data
      );

      const convertToTreewithKeysforDataGrid = (jsonData: any) => {
        const map = new Map();
        const result: any = [];

        jsonData.forEach((item: any) => {
          map.set(item.id, { ...item, children: [] });
        });

        jsonData.forEach((item: OrganizationDTO) => {
          const parent = map.get(item.parentId);
          if (parent) {
            parent.children.push(map.get(item.id));
          } else {
            result.push(map.get(item.id));
          }
        });
        function convertNode(item: any, index = "") {
          if (item === null || item === undefined) return;
          const key = `${index}`;
          const permissionSet: PermissionSet | null = getPermissionSet(item);

          return {
            key: key,
            data: {
              name: item?.name,
              user: item?.totalUserCount,
              organizationId: item?.id,
              groupId: item?.groupId,
            },
            selectable:
              permissionSet === null
                ? false
                : permissionSet?.can_read_organisation_user,

            parentId: item?.parentId,
            //isAdmin: canViewAllOrgs(userPermissions) ? true : item.isAdmin,
            permissionSet: permissionSet,
            version: item?.version,
            children: item?.children?.map((child: any, childIndex: any) =>
              convertNode(child, key + `-${childIndex}`)
            ),
          };
        }
        return result.map((item: any, index: any) => convertNode(item, index));
      };

      const convertToTreewithKeysValueGrid: any =
        convertToTreewithKeysforDataGrid(response.data);

      return {
        originalData: response.data,
        convertedHierarchalData: convertedData,
        hierarchicalOrganizationwKey: convertToTreewithKeysValue,
        treeTableOrganization: convertToTreewithKeysValueGrid,
      };
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const deleteOrganizationbyID = createAsyncThunk(
  "organization/deleteOrganizationbyID",

  async (
    orgInfo: { organizationId: UUID; tenantId: string },
    { rejectWithValue }
  ) => {
    const organizationId = orgInfo.organizationId;
    const tenantId = orgInfo.tenantId;
    try {
      const config = {
        headers: {
          // id: organizationId,
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };
      const response: AxiosResponse<any> = await axios.delete(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.TenantController}/${tenantId}/${ConfigurationVariables.OrganisationController}/${organizationId}`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

interface createOrg {
  organizationName: string;
  groupId: string;
  organizationId: string;
  parentOrganizationId: UUID;
  tenantId: string;
  version: number;
}
interface editOrg {
  organizationName: string;
  groupId: string;
  parentOrganizationId: UUID;
  tenantId: string;
  organizationId: UUID;
  version: number;
}

export const createOrganizationbyID = createAsyncThunk(
  "organization/createOrganizationbyID",

  async (data: createOrg, { rejectWithValue }) => {
    try {
      const postData: any = {
        name: data.organizationName,
        groupId: data.groupId,
        organizationId: data.organizationId,
        parentId: data.parentOrganizationId,
        version: data.version,
      };

      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response: AxiosResponse<any> = await axios.post(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.TenantController}/${data.tenantId}/${ConfigurationVariables.OrganisationController}`,
        postData,
        config
      );
      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

export const editOrganizationbyID = createAsyncThunk(
  "organization/deleteOrganizationbyID",

  async (data: editOrg, { rejectWithValue }) => {
    try {
      const postData: any = {
        name: data.organizationName,
        groupId: data.groupId,
        organizationId: data.organizationId,
        parentId: data.parentOrganizationId,
        version: data.version,
      };

      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };
      const response: AxiosResponse<any> = await axios.put(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.TenantController}/${data.tenantId}/${ConfigurationVariables.OrganisationController}/${data.organizationId}`,
        postData,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

/// Function to fetch Tenant id from user token
interface UserToken {
  sub: number;
  ext: {
    tenant_id: string;
  };
}
const isUserToken = (user: any): user is UserToken => {
  return typeof user === "object" && "sub" in user && "ext" in user;
};

//   if (
//     (sessionStorage.getItem("oidc.default") as any) === undefined ||
//     (sessionStorage.getItem("oidc.default") as any) === null ||
//     (sessionStorage.getItem("oidc.default") as any) === ""
//   ) {
//     return "";
//   }
//   const tokenBearer: string = JSON.parse(
//     sessionStorage.getItem("oidc.default") as any
//   )["tokens"]["accessToken"];
//   if (tokenBearer === undefined || tokenBearer === null || tokenBearer === "") {
//     throw new Error("Access token is not in valid format");
//   }
//   const decode = jwt_decode(tokenBearer);
//   if (!isUserToken(decode)) {
//     throw new Error("invalid token object");
//   }
//   return decode.ext.tenant_id;
// };

// export const getUserByTenantIDCutomerID = createAsyncThunk(
//   "organization/getUserByTenantIDCutomerID",

//   async (
//     sendOrgData: { userId: string; tenantId: string },
//     { rejectWithValue }
//   ) => {
//     try {
//       const config = {
//         headers: {
//           tenantId: sendOrgData.userId,
//           Accept: "application/json",
//           "Content-Type": "application/json",
//           Authorization: "Bearer " + getAccessToken(),
//           isUserCountRequired: false,
//           projectId: sendOrgData.projectId,
//         },
//       };
//       const response: AxiosResponse<any> = await axios.get(
//         `${ConfigurationVariables.UserMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.userVersion}/${ConfigurationVariables.UserController}/${sendOrgData.userId}`,
//         config
//       );

//       return {
//         currentUserData: response.data,
//       };
//     } catch (err: any) {
//       return rejectWithValue(err.response);
//     }
//   }
// );

//// Upload logo for organization
export const uploadOrgLogoDetails = createAsyncThunk(
  "organization/uploadOrgLogoDetails",
  async (parameters: any, { rejectWithValue }) => {
    try {
      let formData = new FormData();
      formData.append("logo", parameters.fileName);
      formData.append("tenantId", parameters.tenantId);
      formData.append("organizationId", parameters.organizationId);

      const config = {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + getAccessToken(),

          "Content-Type": "multipart/form-data",
        },
      };
      const {
        TenantMgmt_Endpoints,
        Api,
        Version,
        TenantController,
        OrganisationController,
      } = ConfigurationVariables;

      const response = await axios.put(
        `${TenantMgmt_Endpoints}/${Api}/${Version}/${TenantController}/${parameters.tenantId}/${OrganisationController}/${parameters.organizationId}/logo`,
        formData,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

//// Upload logo fro organization
export const fetchLogoOrganization = createAsyncThunk(
  "organization/fetchLogoOrganization",
  async (
    orgInfo: { organizationId: UUID; tenantId: string },
    { rejectWithValue }
  ) => {
    const organizationId = orgInfo.organizationId;
    const tenantId = orgInfo.tenantId;
    try {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      };

      const response = await axios.get(
        `${ConfigurationVariables.TenantMgmt_Endpoints}/${ConfigurationVariables.Api}/${ConfigurationVariables.Version}/${ConfigurationVariables.TenantController}/${tenantId}/${ConfigurationVariables.OrganisationController}/${organizationId}/logo`,
        config
      );

      if (response.status !== 200) {
        return rejectWithValue(response);
      }

      return response;
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

const canViewAllOrgs = (userPermissions: UserPermissions) => {
  return (
    userPermissions?.can_create_user && userPermissions?.can_create_organisation
  );
};

const getPermissionSet = (item: any): PermissionSet | null => {
  return item?.permissionSet === null ? null : item?.permissionSet?.result;
};
